import { Box, Group, Image, Loader, Stack, Text } from "@mantine/core";
import { Outlet, useLocation } from "react-router-dom";
import { getAssetUrl } from "@utils/index";
import { useAppNavigate } from "@/apps/global/navigate";
import { useEffect, useState } from "react";
import {
	ChooseStorePopup,
	ReservationPopup,
} from "@/apps/liff/pages/ec-page/components";
import { StoresResponseDTO, UserApi } from "@utils/api/ec";
import liff from "@line/liff";
import { useUserEvent } from "@/apps/@hooks/useUserEvent";
import { useLoadingStore } from "@/stores/useLoadingStores";
import { useChooseStoreOpen } from "@/apps/liff/pages/ec-page/hook/useChooseStoreOpen";
import MessengerPopup from "@/apps/liff/pages/ec-page/components/MessengerPopup";

export default function EcLayout() {
	const { navigateToEcHome, navigateToEcCollect } = useAppNavigate();
	const { pathname } = useLocation();
	const isCollectList = pathname.includes("/collectList");
	const { logEvent, normalizeLogPath } = useUserEvent();
	const { isLoading, setIsLoading } = useLoadingStore();
	const {
		isChooseStoreOpen,
		setChooseStoreOpen,
		chooseStoreType,
		setChooseStoreType,
		chooseStoreStyleCode,
		setChooseStoreStyleCode,
	} = useChooseStoreOpen();
	const [onlineOpen, setOnlineOpen] = useState(false);
	const [messageOpen, setMessageOpen] = useState(false);
	const [messageText, setMessageText] = useState("");
	const [messageConfirmText, setMessageConfirmText] = useState("");
	const [gotoOA, setGotoOA] = useState(false);
	const [stores, setStores] = useState<StoresResponseDTO[]>([]);
	const handleConsult = async () => {
		if (isLoading) {
			return;
		}
		logEvent({
			category: "cta",
			path: normalizeLogPath(),
			action: "consult",
			event: "cta_consult",
		});
		try {
			setIsLoading(true);
			setChooseStoreStyleCode("consult");
			setChooseStoreType("consult");
			const res = await UserApi.sendOaMessage("consult", "consult");
			if (res.status === 1) {
				setOnlineOpen(true);
			} else if (res.status === 0) {
				setChooseStoreOpen(true);
			}
		} catch (e) {
			console.error("sendOaMessage failed", e);
		} finally {
			setIsLoading(false);
		}
	};
	const handleOnlineClose = () => {
		liff.openWindow({
			url: process.env.REACT_APP_LINE_OA_URL ?? "",
			external: false,
		});
	};

	const getStores = async () => {
		try {
			setIsLoading(true);
			const res = await UserApi.getStores();
			if (res.data) {
				setStores(res.data);
			}
		} catch (e) {
			console.error("sendOaMessage failed", e);
		} finally {
			setIsLoading(false);
		}
	};

	const handleChooseStore = async (selectedValue: string | null) => {
		if (selectedValue === null || selectedValue === "") {
			return;
		}
		setChooseStoreOpen(false);
		setIsLoading(true);
		setGotoOA(false);
		try {
			const res = await UserApi.storeManagerAssignSaForNewCustomer(
				chooseStoreStyleCode,
				chooseStoreType,
				selectedValue,
			);
			if (res.status === 1) {
				setGotoOA(true);
				setMessageConfirmText("前往官方帳號聊天室");
				setMessageText("請稍候，\n" + "正在為您安排專屬服務人員 。");
				setMessageOpen(true);
			} else {
				setMessageText("系統錯誤\n" + "請重新嘗試，或聯繫客服處理");
				setMessageOpen(true);
			}
		} catch (e) {
			console.error("sendOaMessage failed", e);
			setMessageText("系統錯誤\n" + "請重新嘗試，或聯繫客服處理");
			setMessageOpen(true);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prevOverflow;
		};
	}, []);

	useEffect(() => {
		getStores();
	}, []);

	return (
		<Stack gap="0" pos="relative" h="100dvh" className="tory-ec-font">
			<Stack pos="fixed" top="0" gap="0" style={{ zIndex: 4 }} bg="white">
				<Image
					src={getAssetUrl("/images/ec-campaign/top-bar-text.jpg")}
					alt="top-text"
					fit="contain"
					h="7.73vw"
					maw="640px"
				/>
				<Group w="100%">
					<Stack
						pos="absolute"
						left="5.32vw"
						gap="0.95vw"
						align="center"
						justify="center"
						style={{ zIndex: 2 }}
					>
						<Image
							src={getAssetUrl("/images/ec-campaign/home-icon.png")}
							alt="love-icon"
							h="4.36vw"
							w="4.36vw"
							onClick={() => {
								logEvent({
									category: "cta",
									path: normalizeLogPath(),
									action: "home",
									event: "cta_home",
								});
								navigateToEcHome();
							}}
						/>
						<Text size="2.06vw">首頁</Text>
					</Stack>
					<Image
						src={getAssetUrl("/images/ec-campaign/logo.jpg")}
						fit="contain"
						maw="640px"
						alt="logo"
					/>
					{!isCollectList && (
						<Stack
							pos="absolute"
							right="5.32vw"
							gap="0.95vw"
							align="center"
							justify="center"
						>
							<Image
								src={getAssetUrl("/images/ec-campaign/love-icon.png")}
								alt="love-icon"
								h="3.61vw"
								w="4.12vw"
								onClick={() => {
									logEvent({
										category: "cta",
										path: normalizeLogPath(),
										action: "wish",
										event: "cta_wish",
									});
									navigateToEcCollect();
								}}
							/>
							<Text size="2.06vw">最愛清單</Text>
						</Stack>
					)}
				</Group>
			</Stack>
			{!isCollectList && !isLoading && (
				<Box
					pos="fixed"
					bottom="5.5vw"
					right="12.11vw"
					style={{ zIndex: 1 }}
					onClick={() => {
						handleConsult();
					}}
				>
					<Image
						src={getAssetUrl("/images/ec-campaign/line-consultation.png")}
						alt="bottom-logo"
						w="25vw"
					/>
				</Box>
			)}
			<Stack
				flex={1}
				pt="2vw"
				pb={isCollectList ? "20.46vw" : "38.315vw"}
				style={{ overflowY: "auto", WebkitOverflowScrolling: "touch" }}
			>
				<Outlet />

				{isLoading && (
					<Stack
						pos="fixed"
						top={0}
						left={0}
						right={0}
						bottom={0}
						justify="center"
						align="center"
						bg="white"
						mt="22.73vw"
						style={{ zIndex: 10 }}
					>
						<Loader color="black" />
					</Stack>
				)}
				<ReservationPopup
					opened={onlineOpen}
					onClose={() => setOnlineOpen(false)}
					onConfirm={handleOnlineClose}
				/>
				<ChooseStorePopup
					chooseStoreOpened={isChooseStoreOpen}
					onClose={() => setChooseStoreOpen(false)}
					onConfirm={(selectedValue) => handleChooseStore(selectedValue)}
					stores={stores}
				/>
				<MessengerPopup
					chooseStoreOpened={messageOpen}
					onClose={() => setMessageOpen(false)}
					onConfirm={(v) => {
						setMessageOpen(false);
						if (gotoOA) {
							liff.openWindow({
								url: process.env.REACT_APP_LINE_OA_URL ?? "",
								external: false,
							});
							return;
						}
					}}
					message={messageText}
					confirmText={messageConfirmText}
				/>
			</Stack>
		</Stack>
	);
}
